import { UseLoading } from "@/common/composition/loading";
import { UseMultiSelectionSimpleItems } from "@/common/composition/multi-selection-simple-items";
import { ICONS } from "@/common/icons.map";
import {
  InformationType,
  InformationTypeTree,
  InfoTypeHelper,
  POPULATION_TYPE,
} from "@/common/models";
import { SharedState } from "@/common/state";
import { groupBy } from "@/fp";
import { computed, Ref, ref } from "@vue/composition-api";
import { BaseDashboardItem } from "./models";
import { info_type_per_l1, get_personal_info_counts } from "./api";
import { pii_state } from "./pii-dashboard/state";

const [
  sensitivities,
  toggle_sensitivity,
  selected_sensitivities,
] = UseMultiSelectionSimpleItems({
  items: [0, 1, 2, 3],
  selected: [0, 1, 2, 3],
  always_keep_one_on: true,
});

const [is_loading, set_loading] = UseLoading(false).as_arr();
const _items: Ref<Record<string, BaseDashboardItem>> = ref({});
const other_count = ref(0);

const _regulated_items: Ref<any[]> = ref([]);

const regulated_items = computed(() =>
  _regulated_items.value.map(([_, items]) => [
    _,
    items.filter((f: any) =>
      selected_sensitivities.value.includes(f.sensitivity)
    ),
  ])
);

const get_regulated_ids = () => {
  const ids = Object.values(
    SharedState.regulated_info_types_by_categories.value
  )
    .flat()
    .map((d) => {
      // @ts-ignore
      if (d.nested && d.nested.length > 0) {
        // @ts-ignore
        return d.nested.map((sub) => sub.id).concat(d.id);
      } else {
        return [d.id];
      }
    })
    .flat();
  return ids;
};

const load_regulated_data = async (payload: {
  session_id: number;
  root_folder_id: string;
}) => {
  const res = await get_personal_info_counts({
    root_folder_id: payload.root_folder_id,
    session_id: payload.session_id,
    personal_info_ids: get_regulated_ids(),
  });
  if (res.success) {
    const indexed_value = res.value.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {} as Record<string, BaseDashboardItem>);

    const all_items = SharedState.regulated_info_types_by_categories.value;

    const mapped_all_items = Object.values(all_items)
      .flat()
      .map((d) => {
        // @ts-ignore
        let nested = [];
        // @ts-ignore
        if (d.nested) {
          // @ts-ignore
          nested = d.nested.map((n) => {
            return {
              id: n.id,
              name: n.labelName,
              parent_code: n.content_code || 7,
              sensitivity: n.sensitivityLevel,
              count: indexed_value[n.id]?.count || 0,
            };
          });
        }
        return {
          id: d.id,
          name: d.labelName,
          // @ts-ignore
          nested: nested,
          parent_code: d.content_code,
          sensitivity: d.sensitivityLevel,
          count: indexed_value[d.id]?.count || 0,
        };
      })
      .sort((a, b) => b.count - a.count);
    _regulated_items.value = Array.from(
      groupBy("parent_code", mapped_all_items).entries()
      // @ts-ignore
    ).sort((a, b) => a[0] - b[0]);
  }
};

const get_other_label = (count: number) => {
  const other_id = SharedState.default_label.value?.id;
  return {
    count,
    id: other_id,
    name: "Other",
    parent_id: other_id,
    type: "LABEL",
  };
};

const display_items = computed(() => {
  const result = SharedState.l0InfoTypes.value
    .filter((f) => f.id !== InfoTypeHelper.REGULATED_ID())
    .map((l0) => {
      return {
        id: l0.id,
        order: l0.l0Order,
        name: l0.labelName,
        available: true,
        icon: ICONS.L0_ICONS.get(l0.code),
        items: SharedState.typesByCategories.value
          .get(l0.id)
          .filter(
            (l: any) =>
              l.generation === 1 &&
              selected_sensitivities.value.includes(l.sensitivityLevel)
          )
          .map((l1: InformationType) => {
            return {
              id: l1.id,
              name: l1.labelName,
              count: _items.value[l1.id]?.count || 0,
              parent_id: l1.parentId,
              type: POPULATION_TYPE.LABEL,
            };
          })
          .sort((a: any, b: any) => b.count - a.count),
      };
    })
    .sort((a, b) => (a.order || -1) - (b.order || -1));
  const other_id = SharedState.default_label.value?.id;
  return result.map((d) => {
    if (d.id === other_id) {
      const should_add_other_to_arr = selected_sensitivities.value.includes(
        SharedState.default_label.value?.sensitivityLevel as number
      );
      if (should_add_other_to_arr) {
        return {
          ...d,
          items: [get_other_label(other_count.value), ...d.items],
        };
      } else {
        return {
          ...d,
          items: d.items,
        };
      }
    }
    return d;
  });
});

const labels_tree_with_counts = ref<
  Array<InformationTypeTree & { count: number }>
>([]);

const set_labels_tree_count = (count_per_label: Record<string, number>) => {
  const tree = SharedState.get_raw_info_types_tree();
  tree.forEach(function set_count_for_label(label) {
    // @ts-ignore
    label.count = label.id in count_per_label ? count_per_label[label.id] : 0;
    if (label.nested && label.nested.length > 0) {
      label.nested.forEach(set_count_for_label);
    }
  });
  return tree as Array<InformationTypeTree & { count: number }>;
};

const get_label_in_tree = (id: string) => {
  const label = SharedState.getInfoType(id)!;
  if (label.generation == 0) {
    return labels_tree_with_counts.value.find((f) => f.id == label.id);
  } else {
    return labels_tree_with_counts.value
      .find((f) => f.id == label.parentId!)
      ?.nested.find((f) => f.id == label.id);
  }
};

const load_data = async (payload: {
  session_id: number;
  root_folder_id: string;
}) => {
  set_loading(true);
  await load_regulated_data(payload);
  const res = await info_type_per_l1(payload);
  pii_state.load_data({
    root_folder_id: payload.root_folder_id,
    session_id: payload.session_id,
  });
  if (res.success) {
    const { other, l1s, all } = res.value;
    labels_tree_with_counts.value = set_labels_tree_count(all);

    other_count.value = other;
    _items.value = l1s
      // @ts-ignore
      .filter((d) => d.parent_id !== "L0")
      // @ts-ignore
      .reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {} as Record<string, BaseDashboardItem>);
  }
  set_loading(false);
};

const destroy = () => {
  _items.value = {};
};

export const state = {
  load_data,
  sensitivities,
  toggle_sensitivity,
  display_items,
  selected_sensitivities,
  regulated_items,
  get_label_in_tree,
  is_loading,
  destroy,
};
